import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import toast from 'react-hot-toast';
import { Eye, EyeOff } from 'lucide-react';
import { validateUsername, getUsernameRequirements } from '../utils/usernameValidator';
import GoogleSignInButton from './GoogleSignInButton'; // Import Google Sign-In Button
import './AuthScreen.css';

function AuthScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth(); // Changed from 'user' to 'currentUser'
  const { featureFlags } = useSettings();
  
  // Get initial auth mode from URL parameter (default to 'login')
  const initialMode = searchParams.get('view') || 'login';
  const [authMode, setAuthMode] = useState(initialMode === 'signup' ? 'signup' : 'login');
  
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  // Assuming login, signup, etc. are part of the AuthContext you provided
  // If these are not in your AuthContext, you'll need to adjust or provide them.
  const { login, signup, sendVerificationCode, verifyCodeAndLogin, resetPassword } = useAuth(); 

  // Note: Redirect logic for already-logged-in users is handled by AuthRedirector
  // This component only handles the auth UI and form submissions

  const [loginData, setLoginData] = useState({
    identifier: '', // email, username, or phone
    password: ''
  });

  const [loginFieldTouched, setLoginFieldTouched] = useState({
    identifier: false,
    password: false
  });

  const [signupData, setSignupData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  const [selectedCountry, setSelectedCountry] = useState('US');

  // Country codes with phone format info
  const countries = [
    { code: 'US', name: 'United States', flag: '🇺🇸', dialCode: '+1', digits: 10, format: '(XXX) XXX-XXXX' },
    { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', dialCode: '+44', digits: 10, format: 'XXXX XXX XXXX' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦', dialCode: '+1', digits: 10, format: '(XXX) XXX-XXXX' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺', dialCode: '+61', digits: 9, format: 'XXX XXX XXX' },
    { code: 'IN', name: 'India', flag: '🇮🇳', dialCode: '+91', digits: 10, format: 'XXXXX XXXXX' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽', dialCode: '+52', digits: 10, format: 'XXX XXX XXXX' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪', dialCode: '+49', digits: 10, format: 'XXX XXXXXXX' },
    { code: 'FR', name: 'France', flag: '🇫🇷', dialCode: '+33', digits: 9, format: 'X XX XX XX XX' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸', dialCode: '+34', digits: 9, format: 'XXX XX XX XX' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹', dialCode: '+39', digits: 10, format: 'XXX XXX XXXX' },
    { code: 'JP', name: 'Japan', flag: '🇯🇵', dialCode: '+81', digits: 10, format: 'XX XXXX XXXX' },
    { code: 'BR', name: 'Brazil', flag: '🇧🇷', dialCode: '+55', digits: 11, format: 'XX XXXXX XXXX' },
    { code: 'OTHER', name: 'Other', flag: '🌍', dialCode: '+', digits: null, format: null }
  ];

  const [passwordlessData, setPasswordlessData] = useState({
    identifier: '', // email or phone
    verificationCode: ''
  });

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Email validation
    if (!validateEmail(loginData.identifier)) {
      toast.error('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      await login(loginData);
      toast.success('Welcome back to The Crapper Mapper! 💩');
      // Navigation will be handled by useEffect hook when currentUser state updates
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordlessLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await sendVerificationCode(passwordlessData.identifier);
      toast.success(result.message);
      setAuthMode('verify');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await verifyCodeAndLogin(passwordlessData.identifier, passwordlessData.verificationCode);
      toast.success('Welcome to The Crapper Mapper! 💩');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone, countryCode) => {
    // Remove all non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, '');
    
    const country = countries.find(c => c.code === countryCode);
    
    // For "Other" country, accept 7-15 digits
    if (countryCode === 'OTHER') {
      return digitsOnly.length >= 7 && digitsOnly.length <= 15;
    }
    
    // Validate based on country's expected digit count
    return country ? digitsOnly.length === country.digits : false;
  };

  const formatPhoneNumber = (value, countryCode) => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    
    const country = countries.find(c => c.code === countryCode);
    
    // For "Other" or unknown countries, don't format - just return digits
    if (!country || countryCode === 'OTHER') {
      return digitsOnly;
    }
    
    // Format based on country
    switch (countryCode) {
      case 'US':
      case 'CA':
        // (XXX) XXX-XXXX
        if (digitsOnly.length <= 3) return digitsOnly;
        if (digitsOnly.length <= 6) return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
        return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
      
      case 'GB':
        // XXXX XXX XXXX
        if (digitsOnly.length <= 4) return digitsOnly;
        if (digitsOnly.length <= 7) return `${digitsOnly.slice(0, 4)} ${digitsOnly.slice(4)}`;
        return `${digitsOnly.slice(0, 4)} ${digitsOnly.slice(4, 7)} ${digitsOnly.slice(7, 11)}`;
      
      case 'AU':
        // XXX XXX XXX
        if (digitsOnly.length <= 3) return digitsOnly;
        if (digitsOnly.length <= 6) return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3)}`;
        return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3, 6)} ${digitsOnly.slice(6, 9)}`;
      
      case 'IN':
        // XXXXX XXXXX
        if (digitsOnly.length <= 5) return digitsOnly;
        return `${digitsOnly.slice(0, 5)} ${digitsOnly.slice(5, 10)}`;
      
      case 'MX':
      case 'IT':
        // XXX XXX XXXX
        if (digitsOnly.length <= 3) return digitsOnly;
        if (digitsOnly.length <= 6) return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3)}`;
        return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3, 6)} ${digitsOnly.slice(6, 10)}`;
      
      case 'JP':
        // XX XXXX XXXX
        if (digitsOnly.length <= 2) return digitsOnly;
        if (digitsOnly.length <= 6) return `${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2)}`;
        return `${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2, 6)} ${digitsOnly.slice(6, 10)}`;
      
      case 'BR':
        // XX XXXXX XXXX
        if (digitsOnly.length <= 2) return digitsOnly;
        if (digitsOnly.length <= 7) return `${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2)}`;
        return `${digitsOnly.slice(0, 2)} ${digitsOnly.slice(2, 7)} ${digitsOnly.slice(7, 11)}`;
      
      default:
        // Default: just add space every 3 digits
        return digitsOnly.match(/.{1,3}/g)?.join(' ') || digitsOnly;
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validation
    // Email is now required for Firebase Auth
    if (!signupData.email) {
      toast.error('Email is required');
      setLoading(false);
      return;
    }

    // Email validation
    if (!validateEmail(signupData.email)) {
      toast.error('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // Phone validation
    if (signupData.phone && !validatePhone(signupData.phone, selectedCountry)) {
      const country = countries.find(c => c.code === selectedCountry);
      const digitText = selectedCountry === 'OTHER' ? '7-15 digits' : `${country?.digits} digits`;
      toast.error(`Please enter a valid phone number (${digitText})`);
      setLoading(false);
      return;
    }
    
    // Store phone with country code
    if (signupData.phone) {
      const country = countries.find(c => c.code === selectedCountry);
      const digitsOnly = signupData.phone.replace(/\D/g, '');
      signupData.phone = `${country?.dialCode}${digitsOnly}`;
    }

    if (signupData.password !== signupData.confirmPassword) {
      toast.error('Passwords do not match');
      setLoading(false);
      return;
    }

    if (signupData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    // Enhanced username validation
    const usernameValidation = validateUsername(signupData.username);
    if (!usernameValidation.isValid) {
      toast.error(usernameValidation.error);
      setLoading(false);
      return;
    }

    try {
      await signup(signupData);
      toast.success('Welcome to The Crapper Mapper! 💩');
      // Navigation will be handled by useEffect hook when currentUser state updates
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginChange = (e) => {
    setLoginData({
      ...loginData,
      [e.target.name]: e.target.value
    });
  };

  const handleLoginBlur = (e) => {
    setLoginFieldTouched({
      ...loginFieldTouched,
      [e.target.name]: true
    });
  };

  const handleSignupChange = (e) => {
    let value = e.target.value;
    
    // Auto-format phone number as user types
    if (e.target.name === 'phone') {
      value = formatPhoneNumber(value, selectedCountry);
    }
    
    setSignupData({
      ...signupData,
      [e.target.name]: value
    });
  };
  
  const handleCountryChange = (e) => {
    const newCountry = e.target.value;
    setSelectedCountry(newCountry);
    
    // Re-format existing phone number for new country
    if (signupData.phone) {
      const digitsOnly = signupData.phone.replace(/\D/g, '');
      const formatted = formatPhoneNumber(digitsOnly, newCountry);
      setSignupData({
        ...signupData,
        phone: formatted
      });
    }
  };

  const handlePasswordlessChange = (e) => {
    setPasswordlessData({
      ...passwordlessData,
      [e.target.name]: e.target.value
    });
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!resetEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    
    if (!validateEmail(resetEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    try {
      await resetPassword(resetEmail);
      toast.success('Password reset email sent! Check your inbox.');
      setShowForgotPassword(false);
      setResetEmail('');
    } catch (error) {
      toast.error(error.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-container">
        <div className="auth-header">
          <div className="auth-logo">
            <img src="/app-icon.png" alt="The Crapper Mapper" />
          </div>
          <h1>The Crapper Mapper</h1>
          <p>Find and rate the best restrooms on the go</p>
        </div>

        {/* TEMPORARILY HIDDEN - Auth mode selector (only password login available) */}
        {/* <div className="auth-mode-selector">
          <button 
            className={`mode-btn ${authMode === 'login' ? 'active' : ''}`}
            onClick={() => setAuthMode('login')}
          >
            Password
          </button>
        </div> */}

        {authMode === 'login' && (
          <>
            <form onSubmit={handleLoginSubmit} className="auth-form">
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  name="identifier"
                  value={loginData.identifier}
                  onChange={handleLoginChange}
                  onBlur={handleLoginBlur}
                  placeholder="Enter your email address"
                  autoComplete="username"
                  required
                />
                {loginFieldTouched.identifier && loginData.identifier && !validateEmail(loginData.identifier) && (
                  <small className="error-text">Please enter a valid email address</small>
                )}
              </div>

              <div className="form-group">
                <label>Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showLoginPassword ? "text" : "password"}
                    name="password"
                    value={loginData.password}
                    onChange={handleLoginChange}
                    onBlur={handleLoginBlur}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowLoginPassword(!showLoginPassword)}
                    aria-label="Toggle password visibility"
                  >
                    {showLoginPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="forgot-password-link">
                <button 
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="link-button"
                >
                  Crap, Forgot Password?
                </button>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary w-full"
                disabled={loading}
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>
            {featureFlags.GoogleSignin && (
              <>
                <div className="or-divider">Or Sign In with</div>
                <GoogleSignInButton />
              </>
            )}
          </>
        )}

        {authMode === 'passwordless' && (
          <form onSubmit={handlePasswordlessLogin} className="auth-form">
            <div className="form-group">
              <label>Email or Phone Number</label>
              <input
                type="text"
                name="identifier"
                value={passwordlessData.identifier}
                onChange={handlePasswordlessChange}
                placeholder="Enter your email or phone number"
                required
              />
            </div>

            <button 
              type="submit" 
              className="btn btn-primary w-full"
              disabled={loading}
            >
              {loading ? 'Sending Code...' : 'Send Verification Code'}
            </button>
          </form>
        )}

        {authMode === 'verify' && (
          <form onSubmit={handleVerificationSubmit} className="auth-form">
            <div className="verification-info">
              <p>We sent a verification code to:</p>
              <strong>{passwordlessData.identifier}</strong>
            </div>
            
            <div className="form-group">
              <label>Verification Code</label>
              <input
                type="text"
                name="verificationCode"
                value={passwordlessData.verificationCode}
                onChange={handlePasswordlessChange}
                placeholder="Enter 6-digit code"
                maxLength="6"
                required
              />
            </div>

            <button 
              type="submit" 
              className="btn btn-primary w-full"
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Verify & Login'}
            </button>

            <button 
              type="button" 
              className="btn btn-secondary w-full mt-2"
              onClick={() => setAuthMode('passwordless')}
            >
              Back
            </button>
          </form>
        )}

        {authMode === 'signup' && (
          <>
            <form onSubmit={handleSignupSubmit} className="auth-form">
              {/* Login link at top for signup view */}
              <div className="auth-top-switch">
                <p>
                  Already have an account?&nbsp;
                  <button 
                    type="button"
                    className="auth-switch"
                    onClick={() => setAuthMode('login')}
                  >
                    Login
                  </button>
                </p>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={signupData.firstName}
                    onChange={handleSignupChange}
                    placeholder="First name"
                    autoComplete="given-name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={signupData.lastName}
                    onChange={handleSignupChange}
                    placeholder="Last name"
                    autoComplete="family-name"
                    required
                  />
                </div>
            </div>

            <div className="form-group">
              <label>Username *</label>
              <input
                type="text"
                name="username"
                value={signupData.username}
                onChange={handleSignupChange}
                placeholder="Choose a fun username"
                maxLength={30}
                autoComplete="username"
                required
              />
              <small className="form-help-text">
                {getUsernameRequirements().description}
              </small>
              {signupData.username && !validateUsername(signupData.username).isValid && (
                <small className="error-text">{validateUsername(signupData.username).error}</small>
              )}
            </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={signupData.email}
                  onChange={handleSignupChange}
                  placeholder="Enter your email"
                  autoComplete="email"
                  required
                />
                {signupData.email && !validateEmail(signupData.email) && (
                  <small className="error-text">Please enter a valid email address</small>
                )}
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <div className="phone-input-wrapper">
                  <button
                    type="button"
                    className="country-code-btn"
                    onClick={() => {
                      const nextIndex = (countries.findIndex(c => c.code === selectedCountry) + 1) % countries.length;
                      handleCountryChange({ target: { value: countries[nextIndex].code } });
                    }}
                    title="Click to change country"
                  >
                    {countries.find(c => c.code === selectedCountry)?.flag} {countries.find(c => c.code === selectedCountry)?.dialCode}
                  </button>
                  <input
                    type="tel"
                    name="phone"
                    value={signupData.phone}
                    onChange={handleSignupChange}
                    placeholder={countries.find(c => c.code === selectedCountry)?.format || 'Phone number'}
                    className="phone-input"
                    autoComplete="tel"
                  />
                </div>
                {!loading && signupData.phone && !validatePhone(signupData.phone, selectedCountry) && (
                  <small className="error-text">
                    Please enter a valid {selectedCountry === 'OTHER' ? 'phone number (7-15 digits)' : `${countries.find(c => c.code === selectedCountry)?.name} phone number`}
                  </small>
                )}
                <small>Click country code to change</small>
              </div>

              <div className="form-group">
                <label>Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={signupData.password}
                    onChange={handleSignupChange}
                    placeholder="Create a password (min 6 characters)"
                    minLength="6"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Confirm Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={signupData.confirmPassword}
                    onChange={handleSignupChange}
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label="Toggle confirm password visibility"
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary w-full"
                disabled={loading}
              >
                {loading ? 'Creating Account...' : 'Sign Up'}
              </button>
            </form>
            {featureFlags.GoogleSignin && (
              <>
                <div className="or-divider">Or Sign In with</div>
                <GoogleSignInButton />
              </>
            )}
          </>
        )}

        <div className="auth-footer">
          {/* Only show signup link in login view */}
          {authMode === 'login' && (
            <p>
              Don't have an account?  
              <button 
                type="button"
                className="auth-switch auth-switch-prominent"
                onClick={() => setAuthMode('signup')}
              >
                Sign Up
              </button>
            </p>
          )}
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="modal-overlay" onClick={() => setShowForgotPassword(false)}>
          <div className="modal-content forgot-password-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reset Password</h2>
              <button 
                className="modal-close"
                onClick={() => setShowForgotPassword(false)}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <p className="modal-description">
                Enter your email address and we'll send you a link to reset your password.
              </p>

              <form onSubmit={handleForgotPassword}>
                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="Enter your email"
                    disabled={loading}
                    required
                  />
                </div>

                <div className="modal-actions">
                  <button 
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setResetEmail('');
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AuthScreen;
